import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { IndianRupee, TrendingUp, TrendingDown, Users, Search, ChevronRight, Download, BookOpen, Scale, FileText, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TABS = [
  { key: 'ledger',    label: 'Client Ledger',   icon: Users },
  { key: 'gst',       label: 'GST Summary',      icon: Receipt },
  { key: 'pnl',       label: 'P & L Statement',  icon: TrendingUp },
  { key: 'trial',     label: 'Trial Balance',    icon: Scale },
];

const COLORS = ['#F59E0B', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4'];

export default function Accounting() {
  const [tab, setTab]                   = useState('ledger');
  const [clients, setClients]           = useState([]);
  const [clientDetail, setClientDetail] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [gstSummary, setGstSummary]     = useState(null);
  const [pnl, setPnl]                   = useState(null);
  const [trial, setTrial]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, gRes, pRes, tRes] = await Promise.all([
          axios.get(`${API}/accounting/client-ledger`, { withCredentials: true }),
          axios.get(`${API}/accounting/gst-summary`,   { withCredentials: true }),
          axios.get(`${API}/accounting/pnl`,           { withCredentials: true }),
          axios.get(`${API}/accounting/trial-balance`, { withCredentials: true }),
        ]);
        setClients(cRes.data); setGstSummary(gRes.data); setPnl(pRes.data); setTrial(tRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const loadClientDetail = async (name) => {
    setSelectedClient(name);
    const res = await axios.get(`${API}/accounting/client-ledger/${encodeURIComponent(name)}`, { withCredentials: true });
    setClientDetail(res.data);
  };

  const filteredClients = clients.filter(c => c.client.toLowerCase().includes(search.toLowerCase()));

  const inputCls = "bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-zinc-300 text-sm focus:outline-none focus:border-amber-500";

  if (loading) return <div className="text-zinc-400 animate-pulse p-8">Loading accounting data...</div>;

  return (
    <div className="space-y-5" data-testid="accounting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50">Accounting & Ledger</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Tally-style accounts, GST, P&L — Tirth Industries</p>
        </div>
        <button onClick={() => window.print()} className="flex items-center gap-1.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-xs px-3 py-2 rounded-xl">
          <Download size={13} /> Export
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Revenue',      value: fmt(pnl?.revenue),         icon: TrendingUp,   color: 'text-green-400',  bg: 'bg-green-500/10' },
          { label: 'Total Expenses',     value: fmt(pnl?.total_expenses),   icon: TrendingDown, color: 'text-red-400',    bg: 'bg-red-500/10' },
          { label: 'Gross Profit',       value: fmt(pnl?.gross_profit),     icon: IndianRupee,  color: 'text-amber-400',  bg: 'bg-amber-500/10' },
          { label: 'Net GST Payable',    value: fmt(gstSummary?.net_payable), icon: Receipt,    color: 'text-blue-400',   bg: 'bg-blue-500/10' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}><Icon size={15} className={color} /></div>
              <span className="text-zinc-400 text-xs">{label}</span>
            </div>
            <div className={`font-mono font-bold text-xl ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 w-fit flex-wrap">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`acc-tab-${key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === key ? 'bg-amber-500 text-black font-bold' : 'text-zinc-400 hover:text-zinc-200'}`}>
            <Icon size={13} />{label}
          </button>
        ))}
      </div>

      {/* ── CLIENT LEDGER ── */}
      {tab === 'ledger' && (
        <div className="flex gap-4">
          {/* Client list */}
          <div className="w-72 flex-shrink-0 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-2.5 text-zinc-500" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-3 py-2 text-zinc-300 text-xs focus:outline-none" />
              </div>
            </div>
            <div className="overflow-y-auto max-h-96">
              {filteredClients.map(c => (
                <button key={c.client} onClick={() => loadClientDetail(c.client)} data-testid={`client-${c.client}`}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-800/50 hover:bg-zinc-800/50 transition-colors ${selectedClient === c.client ? 'bg-amber-500/10 border-l-2 border-l-amber-500' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-200 text-sm font-medium">{c.client}</span>
                    <ChevronRight size={13} className="text-zinc-600" />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-zinc-500 text-xs">{c.orders} orders</span>
                    <span className={`text-xs font-mono font-semibold ${c.outstanding > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {c.outstanding > 0 ? `Due: ${fmt(c.outstanding)}` : 'Paid'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Ledger detail */}
          <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            {clientDetail ? (
              <>
                <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-zinc-50">{clientDetail.client}</h3>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-zinc-500 text-xs">Invoiced: <span className="text-zinc-300 font-mono">{fmt(clientDetail.total_invoiced)}</span></span>
                      <span className="text-zinc-500 text-xs">Paid: <span className="text-green-400 font-mono">{fmt(clientDetail.total_paid)}</span></span>
                      <span className={`text-xs font-semibold ${clientDetail.outstanding_balance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        Outstanding: {fmt(Math.abs(clientDetail.outstanding_balance))} {clientDetail.outstanding_balance > 0 ? '(Due)' : '(Advance)'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-zinc-800 bg-zinc-800/30">
                      {['Date', 'Particulars', 'Voucher Ref', 'Type', 'Debit (₹)', 'Credit (₹)', 'Balance (₹)'].map(h => (
                        <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {clientDetail.entries.map((e, i) => (
                        <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                          <td className="px-4 py-2.5 font-mono text-zinc-500 text-xs">{e.date}</td>
                          <td className="px-4 py-2.5 text-zinc-300 text-xs max-w-48 truncate">{e.particulars}</td>
                          <td className="px-4 py-2.5 font-mono text-zinc-600 text-xs">{e.voucher_ref || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-mono font-bold ${e.type === 'Dr' ? 'text-red-400 bg-red-900/20' : 'text-green-400 bg-green-900/20'}`}>{e.type}</span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-red-400 text-xs text-right">{e.debit > 0 ? fmt(e.debit) : '—'}</td>
                          <td className="px-4 py-2.5 font-mono text-green-400 text-xs text-right">{e.credit > 0 ? fmt(e.credit) : '—'}</td>
                          <td className={`px-4 py-2.5 font-mono text-xs text-right font-semibold ${e.balance > 0 ? 'text-red-300' : 'text-green-300'}`}>{fmt(Math.abs(e.balance))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="border-t-2 border-zinc-700 bg-zinc-800/40">
                      <td colSpan={4} className="px-4 py-3 text-zinc-300 text-xs font-bold">CLOSING BALANCE</td>
                      <td className="px-4 py-3 font-mono text-red-400 text-xs text-right font-bold">{fmt(clientDetail.total_invoiced)}</td>
                      <td className="px-4 py-3 font-mono text-green-400 text-xs text-right font-bold">{fmt(clientDetail.total_paid)}</td>
                      <td className={`px-4 py-3 font-mono text-sm text-right font-bold ${clientDetail.outstanding_balance > 0 ? 'text-red-400' : 'text-green-400'}`}>{fmt(Math.abs(clientDetail.outstanding_balance))}</td>
                    </tr></tfoot>
                  </table>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-zinc-600">
                <Users size={40} className="mb-3" />
                <p className="text-sm">Select a client to view ledger</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── GST SUMMARY ── */}
      {tab === 'gst' && gstSummary && (
        <div className="space-y-4">
          {/* Top summary */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'GST Output (Sales)',     value: gstSummary.gst_output,   color: 'text-green-400',  note: 'Collected from customers' },
              { label: 'GST Input Credit',       value: gstSummary.gst_input,    color: 'text-blue-400',   note: 'Paid to vendors (claimable)' },
              { label: 'Net GST Payable to Govt',value: gstSummary.net_payable,  color: gstSummary.net_payable > 0 ? 'text-red-400' : 'text-green-400', note: 'Output − Input' },
            ].map(({ label, value, color, note }) => (
              <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <p className="text-zinc-500 text-xs">{label}</p>
                <p className="text-zinc-400 text-xs mt-0.5 italic">{note}</p>
                <p className={`font-mono font-bold text-2xl mt-2 ${color}`}>{fmt(value)}</p>
              </div>
            ))}
          </div>

          {/* GST Output breakdown */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-zinc-800"><h3 className="font-heading text-sm font-semibold text-zinc-50">Output GST (Sales) — Rate-wise</h3></div>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-zinc-800 text-zinc-500">
                  {['Rate', 'Taxable Value', 'CGST', 'SGST', 'Total GST'].map(h => <th key={h} className="text-left px-4 py-2.5 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(gstSummary.output_by_rate || []).map((r, i) => (
                    <tr key={i} className="border-b border-zinc-800/50">
                      <td className="px-4 py-2.5 font-mono text-amber-400 font-bold">{r.key}</td>
                      <td className="px-4 py-2.5 font-mono text-zinc-300">{fmt(r.taxable)}</td>
                      <td className="px-4 py-2.5 font-mono text-green-400">{fmt(r.cgst)}</td>
                      <td className="px-4 py-2.5 font-mono text-green-400">{fmt(r.sgst)}</td>
                      <td className="px-4 py-2.5 font-mono text-green-400 font-semibold">{fmt(r.total)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-zinc-700 bg-zinc-800/30 font-semibold">
                    <td className="px-4 py-2.5 text-zinc-300 text-xs">TOTAL</td>
                    <td className="px-4 py-2.5 font-mono text-zinc-200">{fmt((gstSummary.output_by_rate||[]).reduce((s,r)=>s+r.taxable,0))}</td>
                    <td className="px-4 py-2.5 font-mono text-green-400">{fmt(gstSummary.gst_output/2)}</td>
                    <td className="px-4 py-2.5 font-mono text-green-400">{fmt(gstSummary.gst_output/2)}</td>
                    <td className="px-4 py-2.5 font-mono text-green-400">{fmt(gstSummary.gst_output)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-zinc-800"><h3 className="font-heading text-sm font-semibold text-zinc-50">Input GST (Purchases) — Rate-wise</h3></div>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-zinc-800 text-zinc-500">
                  {['Rate', 'Taxable Value', 'CGST', 'SGST', 'Total GST'].map(h => <th key={h} className="text-left px-4 py-2.5 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(gstSummary.input_by_rate || []).map((r, i) => (
                    <tr key={i} className="border-b border-zinc-800/50">
                      <td className="px-4 py-2.5 font-mono text-amber-400 font-bold">{r.key}</td>
                      <td className="px-4 py-2.5 font-mono text-zinc-300">{fmt(r.taxable)}</td>
                      <td className="px-4 py-2.5 font-mono text-blue-400">{fmt(r.cgst)}</td>
                      <td className="px-4 py-2.5 font-mono text-blue-400">{fmt(r.sgst)}</td>
                      <td className="px-4 py-2.5 font-mono text-blue-400 font-semibold">{fmt(r.total)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-zinc-700 bg-zinc-800/30 font-semibold">
                    <td className="px-4 py-2.5 text-zinc-300 text-xs">TOTAL</td>
                    <td className="px-4 py-2.5 font-mono text-zinc-200">{fmt((gstSummary.input_by_rate||[]).reduce((s,r)=>s+r.taxable,0))}</td>
                    <td className="px-4 py-2.5 font-mono text-blue-400">{fmt(gstSummary.gst_input/2)}</td>
                    <td className="px-4 py-2.5 font-mono text-blue-400">{fmt(gstSummary.gst_input/2)}</td>
                    <td className="px-4 py-2.5 font-mono text-blue-400">{fmt(gstSummary.gst_input)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* GST Payable summary box */}
          <div className={`rounded-xl border-2 p-5 flex items-center justify-between ${gstSummary.net_payable > 0 ? 'border-red-700 bg-red-950/30' : 'border-green-700 bg-green-950/30'}`}>
            <div>
              <p className={`font-heading font-bold text-lg ${gstSummary.net_payable > 0 ? 'text-red-300' : 'text-green-300'}`}>
                {gstSummary.net_payable > 0 ? 'Net GST Payable to Government' : 'Excess Input Credit (Refundable)'}
              </p>
              <p className="text-zinc-500 text-xs mt-1">Output GST {fmt(gstSummary.gst_output)} − Input Credit {fmt(gstSummary.gst_input)}</p>
            </div>
            <p className={`font-mono font-bold text-3xl ${gstSummary.net_payable > 0 ? 'text-red-400' : 'text-green-400'}`}>{fmt(Math.abs(gstSummary.net_payable))}</p>
          </div>
        </div>
      )}

      {/* ── P&L STATEMENT ── */}
      {tab === 'pnl' && pnl && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* P&L Table */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                <FileText size={15} className="text-amber-400" />
                <h3 className="font-heading text-sm font-bold text-zinc-50">Profit & Loss Statement</h3>
              </div>
              <div className="p-5 space-y-4">
                {/* Income */}
                <div>
                  <p className="text-green-400 text-xs font-bold mb-2 uppercase tracking-wider">Income</p>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-300 text-sm">Sales Revenue</span>
                    <span className="font-mono text-green-400 font-semibold">{fmt(pnl.revenue)}</span>
                  </div>
                  <div className="flex justify-between py-1.5">
                    <span className="text-zinc-400 text-xs font-semibold">Total Income</span>
                    <span className="font-mono text-green-400 text-sm font-bold">{fmt(pnl.revenue)}</span>
                  </div>
                </div>

                {/* Expenses */}
                <div>
                  <p className="text-red-400 text-xs font-bold mb-2 uppercase tracking-wider">Expenses</p>
                  {Object.entries(pnl.expense_breakdown || {}).map(([cat, amt]) => (
                    <div key={cat} className="flex justify-between py-1.5 border-b border-zinc-800/50">
                      <span className="text-zinc-300 text-sm">{cat}</span>
                      <span className="font-mono text-red-400">{fmt(amt)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-1.5 mt-1">
                    <span className="text-zinc-400 text-xs font-semibold">Total Expenses</span>
                    <span className="font-mono text-red-400 text-sm font-bold">{fmt(pnl.total_expenses)}</span>
                  </div>
                </div>

                {/* Net Profit */}
                <div className={`rounded-xl p-4 ${pnl.gross_profit >= 0 ? 'bg-green-900/20 border border-green-800' : 'bg-red-900/20 border border-red-800'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className={`font-heading font-bold text-lg ${pnl.gross_profit >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {pnl.gross_profit >= 0 ? 'Net Profit' : 'Net Loss'}
                      </p>
                      <p className="text-zinc-500 text-xs">Margin: {pnl.gross_margin_pct}%</p>
                    </div>
                    <p className={`font-mono font-bold text-2xl ${pnl.gross_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmt(Math.abs(pnl.gross_profit))}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Expense Chart */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="font-heading text-sm font-semibold text-zinc-50 mb-4">Expense Breakdown</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={Object.entries(pnl.expense_breakdown || {}).map(([name, value]) => ({ name, value }))}
                    cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                    {Object.keys(pnl.expense_breakdown || {}).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '11px' }} formatter={v => [fmt(v)]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-3">
                {Object.keys(pnl.expense_breakdown || {}).map((cat, i) => (
                  <div key={cat} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-zinc-400 text-xs">{cat}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TRIAL BALANCE ── */}
      {tab === 'trial' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading text-sm font-bold text-zinc-50 flex items-center gap-2"><Scale size={15} className="text-amber-400"/>Trial Balance</h3>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-3 py-1 rounded-full ${
                Math.abs(trial.reduce((s,r)=>s+r.debit,0) - trial.reduce((s,r)=>s+r.credit,0)) < 1
                ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-red-900/40 text-red-300 border border-red-700'
              }`}>
                {Math.abs(trial.reduce((s,r)=>s+r.debit,0) - trial.reduce((s,r)=>s+r.credit,0)) < 1 ? 'Balanced' : 'Mismatch'}
              </span>
              <a href={`${API}/accounting/trial-balance/pdf`} target="_blank" rel="noopener noreferrer"
                data-testid="trial-balance-pdf-btn"
                className="flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-400 hover:bg-zinc-800 px-3 py-1.5 rounded-xl transition-colors">
                <Download size={12} /> Download PDF
              </a>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-zinc-800 bg-zinc-800/30">
              {['Account', 'Type', 'Debit (₹)', 'Credit (₹)', 'Net Balance'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-5 py-3">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {trial.map((row, i) => {
                const typeColor = { Asset: 'text-blue-400', Liability: 'text-purple-400', Income: 'text-green-400', Expense: 'text-red-400', Equity: 'text-amber-400' };
                return (
                  <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                    <td className="px-5 py-3 text-zinc-200 text-sm font-medium">{row.account}</td>
                    <td className="px-5 py-3"><span className={`text-xs ${typeColor[row.type] || 'text-zinc-400'}`}>{row.type}</span></td>
                    <td className="px-5 py-3 font-mono text-red-400">{row.debit > 0 ? fmt(row.debit) : '—'}</td>
                    <td className="px-5 py-3 font-mono text-green-400">{row.credit > 0 ? fmt(row.credit) : '—'}</td>
                    <td className={`px-5 py-3 font-mono font-semibold ${row.balance > 0 ? 'text-amber-400' : 'text-zinc-400'}`}>{fmt(row.balance)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr className="border-t-2 border-zinc-700 bg-zinc-800/40 font-bold">
              <td className="px-5 py-3 text-zinc-200">TOTAL</td>
              <td/>
              <td className="px-5 py-3 font-mono text-red-400">{fmt(trial.reduce((s,r)=>s+r.debit,0))}</td>
              <td className="px-5 py-3 font-mono text-green-400">{fmt(trial.reduce((s,r)=>s+r.credit,0))}</td>
              <td/>
            </tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
